import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type { PostEventSurvey, SurveyKey } from '../../types/database';

export interface SurveyQuestionDefinition {
  key: string;
  label: string;
  required: boolean;
}

export const PRE_EVENT_SURVEY_QUESTIONS: SurveyQuestionDefinition[] = [
  {
    key: 'given_out_gospel_tract_or_scripture',
    label: 'Have you ever given out a Gospel tract/scripture?',
    required: true,
  },
  {
    key: 'prayed_with_unfamiliar_person',
    label: 'Have you ever prayed with someone you are unfamiliar with?',
    required: true,
  },
  {
    key: 'been_on_mission_trip_before',
    label: 'Have you ever been on a mission trip before?',
    required: true,
  },
  {
    key: 'presented_plan_of_salvation_before',
    label: 'Have you ever presented the plan of salvation to someone before?',
    required: true,
  },
  {
    key: 'shared_testimony_outside_church',
    label: 'Have you ever shared your testimony with someone outside of church?',
    required: true,
  },
];

export const POST_EVENT_SURVEY_QUESTIONS: SurveyQuestionDefinition[] = [
  {
    key: 'given_out_gospel_tract_or_scripture_since_event',
    label: 'Since attending this event, have you given out a Gospel tract/scripture?',
    required: true,
  },
  {
    key: 'prayed_with_unfamiliar_person_since_event',
    label: 'Since attending this event, have you prayed with someone you are unfamiliar with?',
    required: true,
  },
  {
    key: 'been_on_mission_trip_since_event',
    label: 'Since attending this event, have you been on a mission trip?',
    required: true,
  },
  {
    key: 'presented_plan_of_salvation_since_event',
    label: 'Since attending this event, have you presented the plan of salvation to someone?',
    required: true,
  },
  {
    key: 'shared_testimony_outside_church_since_event',
    label: 'Since attending this event, have you shared your testimony with someone outside of church?',
    required: true,
  },
];

export interface SurveyTokenContext {
  id: string;
  survey_key: SurveyKey;
  sent_at: string | null;
  submitted_at: string | null;
  event_title: string | null;
  event_slug: string | null;
  respondent_name: string;
}

export interface SurveyRecordWithEvent extends PostEventSurvey {
  event_application?: {
    id: string;
    event?: {
      title: string;
      slug: string;
    } | null;
  };
}

async function sendSurvey(applicationId: string, surveyKey: SurveyKey): Promise<void> {
  const { data, error } = await supabase.functions.invoke('survey', {
    body: {
      action: 'create_and_send',
      application_id: applicationId,
      survey_key: surveyKey,
    },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
}

export async function sendPreEventSurvey(applicationId: string): Promise<void> {
  return sendSurvey(applicationId, 'pre_event');
}

export async function sendPostEventSurvey(applicationId: string): Promise<void> {
  return sendSurvey(applicationId, 'post_event');
}

export async function getSurveyByToken(token: string): Promise<SurveyTokenContext> {
  const { data, error } = await supabase.functions.invoke('survey', {
    body: {
      action: 'get_by_token',
      token,
    },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  if (!data?.data) throw new Error('Survey not found');
  return data.data as SurveyTokenContext;
}

export async function getPreEventSurveyByToken(token: string): Promise<SurveyTokenContext> {
  return getSurveyByToken(token);
}

export async function getPostEventSurveyByToken(token: string): Promise<SurveyTokenContext> {
  return getSurveyByToken(token);
}

export async function submitSurveyByToken(
  token: string,
  responsePayload: Record<string, boolean>,
  submissionSource: string
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('survey', {
    body: {
      action: 'submit_by_token',
      token,
      response_payload: responsePayload,
      submission_source: submissionSource,
    },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
}

export async function submitPreEventSurvey(
  token: string,
  responsePayload: Record<string, boolean>
): Promise<void> {
  return submitSurveyByToken(token, responsePayload, 'pre_event_survey_page');
}

export async function submitPostEventSurvey(
  token: string,
  responsePayload: Record<string, boolean>
): Promise<void> {
  return submitSurveyByToken(token, responsePayload, 'post_event_survey_page');
}

export async function getSurveyForApplication(applicationId: string): Promise<PostEventSurvey[]> {
  const { data, error } = await supabase
    .from('post_event_surveys')
    .select('*')
    .eq('event_application_id', applicationId)
    .order('survey_key', { ascending: true });
  if (error) throw normalizeSupabaseError(error);
  return (data || []) as PostEventSurvey[];
}

export async function getSurveysForApplications(applicationIds: string[]): Promise<PostEventSurvey[]> {
  if (applicationIds.length === 0) return [];
  const { data, error } = await supabase
    .from('post_event_surveys')
    .select('*')
    .in('event_application_id', applicationIds);
  if (error) throw normalizeSupabaseError(error);
  return (data || []) as PostEventSurvey[];
}

export async function getSurveysForUser(userId: string): Promise<SurveyRecordWithEvent[]> {
  const { data: profile, error: profileError } = await supabase
    .from('attendee_profiles')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  if (profileError) throw normalizeSupabaseError(profileError);
  if (!profile) return [];

  const { data, error } = await supabase
    .from('post_event_surveys')
    .select(`
      *,
      event_application:event_applications(
        id,
        event:events(
          title,
          slug
        )
      )
    `)
    .eq('attendee_profile_id', profile.id)
    .order('sent_at', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return (data || []) as SurveyRecordWithEvent[];
}
